// Connect Email Prompt Component
// Shows when no email connection exists

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mail, 
  CheckCircle, 
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  AlertTriangle,
  Settings
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import GmailOAuth from '@/components/auth/GmailOAuth';
import gmailConfig from '@/config/gmail';

/**
 * Connect Email Prompt Component
 * Encourages users to connect their Gmail account
 */
const ConnectEmailPrompt = ({ onConnect, onError }) => {
  // Check if Gmail OAuth is configured
  const isConfigured = gmailConfig.oauth.clientId && gmailConfig.oauth.clientId !== 'your_google_oauth_client_id_here';
  
  // Debug: Log configuration status
  console.log('ConnectEmailPrompt: OAuth configuration status:', {
    clientId: gmailConfig.oauth.clientId,
    redirectUri: gmailConfig.oauth.redirectUri,
    scopes: gmailConfig.oauth.scopes,
    isConfigured
  });

  return (
    <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Mail className="h-5 w-5" />
          Connect Your Gmail Account
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Configuration Warning */}
        {!isConfigured && (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">Gmail integration is not configured</p>
                <p className="text-sm">
                  To enable Gmail integration, please set up your Google OAuth credentials in the .env.local file.
                </p>
                <div className="text-xs text-gray-600">
                  <p>Required environment variables:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>VITE_GOOGLE_OAUTH_CLIENT_ID</li>
                    <li>VITE_GOOGLE_OAUTH_REDIRECT_URI</li>
                    <li>VITE_GOOGLE_OAUTH_SCOPES</li>
                  </ul>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {/* Main Description */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Automatically Track Camp Activities
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Connect your Gmail to automatically scan for camp registrations, 
            confirmations, and reminders. We'll help you keep track of all your kids' activities.
          </p>
        </div>

        {/* Benefits List */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 text-center">What we'll find:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">Camp registration confirmations</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">Activity reminders and updates</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">Payment receipts and invoices</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">Schedule changes and notifications</span>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 text-center">Features:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg border">
              <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-900 mb-1">Daily Sync</h5>
              <p className="text-xs text-gray-600">
                Automatically check for new emails every day
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <Shield className="h-6 w-6 text-green-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-900 mb-1">Secure & Private</h5>
              <p className="text-xs text-gray-600">
                We only read emails, never send or modify
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg border">
              <Sparkles className="h-6 w-6 text-brand-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-900 mb-1">Smart Detection</h5>
              <p className="text-xs text-gray-600">
                Automatically finds camp-related information
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center space-y-4">
          {isConfigured ? (
            <GmailOAuth
              onSuccess={(oauthData) => {
                console.log('🟢 ConnectEmailPrompt: Gmail OAuth SUCCESS!', {
                  hasData: !!oauthData,
                  email: oauthData?.email,
                  hasAccessToken: !!oauthData?.access_token
                });
                onConnect(oauthData);
              }}
              onError={(error) => {
                console.error('🔴 ConnectEmailPrompt: Gmail OAuth FAILED:', error);
                onError(error);
              }}
              buttonText="Connect Gmail Account"
              className="flex justify-center"
              showConsentMessage={false}
            />
          ) : (
            <Button 
              size="lg"
              disabled={true}
              className="px-8 py-3 bg-gray-300 text-gray-500 cursor-not-allowed"
            >
              <Mail className="h-5 w-5 mr-2" />
              Gmail Not Configured
            </Button>
          )}
          
          <p className="text-xs text-gray-500">
            You can disconnect or change settings anytime in this section
          </p>
        </div>

        {/* Privacy Notice */}
        <div className="p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-1">Your Privacy is Protected</p>
              <p>
                We use secure OAuth authentication and only access the minimum required permissions. 
                Your email data is processed securely and never shared with third parties. 
                You can revoke access at any time.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConnectEmailPrompt;
